import { supabase } from '../lib/supabase';
import { rubroVisible } from '../lib/onboardingPendiente';

/** Una marca como la trae la consulta de `brand_profiles` (no-draft, ya ordenada). */
export interface MarcaDeLaLista {
  id: string;
  rubro: string | null;
  is_default: boolean;
}

/** La marca activa resuelta, con lo que necesitan sus consumidores. */
export interface MarcaActiva {
  /** `null` = la empresa no tiene marcas. Quien filtre por marca NO debe filtrar. */
  id: string | null;
  /** Rubro de ESA marca (`brand_profiles.rubro`), sin fallback al de la empresa. */
  rubro: string | null;
  /** `true` con dos o más marcas — el guard de `marcaParaFiltrar` (`utils/filtroDeMarca`). */
  variasMarcas: boolean;
}

/**
 * El desempate de "marca activa", **puro** — ADR-2026-09-13-el-cambio-de-marca-no-llega-al-chat.
 *
 * Estaba inline acá y repetido en `useBrandProfile` y `useRubroMarcaActiva`; se extrae para
 * poder testearlo sin base y para que quien necesite el rubro o la cantidad de marcas no
 * tenga que resolver la marca otra vez. El orden y la validación no cambian.
 *
 * `list` viene en el orden de la consulta: `is_default` primero, después por `created_at`.
 */
export function elegirMarcaActiva(
  list: MarcaDeLaLista[],
  currentBrandId: string | null | undefined,
): MarcaActiva {
  if (list.length === 0) return { id: null, rubro: null, variasMarcas: false };
  // Se valida contra la lista a propósito: un `current_brand_id` que apunta a una marca
  // borrada, pasada a draft o de otra empresa no puede ser la marca activa, y filtrar por
  // él dejaría el catálogo vacío en vez de mostrar la marca default.
  const elegida =
    (currentBrandId ? list.find((b) => b.id === currentBrandId) : undefined) ??
    list.find((b) => b.is_default) ??
    list[0];
  return {
    id: elegida.id,
    // El centinela del alta por link no es un rubro: es que todavía no se preguntó
    // (`lib/onboardingPendiente`). De acá sale el entorno del chat, y el agente lo
    // estaba contando crudo —"Rubro: sin-definir"— en el prompt de cada turno.
    rubro: rubroVisible(elegida.rubro),
    variasMarcas: list.length > 1,
  };
}

/**
 * Marca activa del usuario para una empresa, con su rubro. **Sólo lectura.**
 *
 * Mismo orden de precedencia que `useBrandProfile` y `useRubroMarcaActiva`:
 *   1) `profiles.current_brand_id` (si sigue siendo una marca no-draft de la empresa)
 *   2) la marca default de la empresa
 *   3) la marca no-draft más vieja
 * El desempate vive en `elegirMarcaActiva`, arriba, puro y testeado.
 *
 * Devuelve `id: null` cuando la empresa no tiene marcas: en ese caso el caller
 * **no** debe filtrar por marca (filtrar por `null` esconde todo el catálogo).
 *
 * A diferencia de `resolveProjectBrandId` (`utils/videoProject`), NO crea una
 * marca default cuando no hay ninguna. Esto se usa desde pantallas de lectura,
 * donde una escritura silenciosa al abrir un listado sería un efecto no deseado.
 *
 * Existe además del hook `useRubroMarcaActiva` porque hay consumidores que no pueden usar
 * un hook que resuelve UNA vez al montar: `EntornoChatAtlas` vive en la raíz de la app, se
 * monta antes del login y el login es una navegación blanda, así que el hook se le quedaría
 * con el resultado de la sesión anónima (ADR-2026-09-13-el-cambio-de-marca-no-llega-al-chat).
 * Acá la resolución la dispara el caller cuando cambia lo que la afecta.
 */
export async function resolveMarcaActiva(companyId: string): Promise<MarcaActiva> {
  const vacia: MarcaActiva = { id: null, rubro: null, variasMarcas: false };
  if (!companyId) return vacia;

  const { data: brandsData } = await supabase
    .from('brand_profiles')
    .select('id, rubro, is_default')
    .eq('id_company', companyId)
    .eq('is_draft', false)
    .order('is_default', { ascending: false })
    .order('created_at', { ascending: true });

  const list = (brandsData as MarcaDeLaLista[] | null) ?? [];
  if (list.length === 0) return vacia;

  const {
    data: { user },
  } = await supabase.auth.getUser();
  // Sin usuario no hay `current_brand_id` que mirar: el desempate cae solo a la default.
  if (!user) return elegirMarcaActiva(list, null);

  const { data: prof } = await supabase
    .from('profiles')
    .select('current_brand_id')
    .eq('id', user.id)
    .maybeSingle();

  return elegirMarcaActiva(
    list,
    (prof as { current_brand_id?: string | null } | null)?.current_brand_id ?? null,
  );
}

/**
 * La marca activa, pero SIN tragarse los errores: `null` = no se pudo saber, y `{ id: null }`
 * = la empresa de verdad no tiene marcas. `resolveMarcaActiva` cae a la marca default si
 * falla una lectura, lo que está bien para filtrar una grilla pero no para ESCRIBIR: un post
 * que va a la marca equivocada es un dato mal puesto (review atlas-code#176,
 * `mandar_a_instagram`). Mismo desempate que el resto de la app (`elegirMarcaActiva`).
 */
export async function resolveMarcaActivaEstricta(
  companyId: string,
): Promise<{ id: string | null } | null> {
  if (!companyId) return null;
  const { data: brandsData, error: alListar } = await supabase
    .from('brand_profiles')
    .select('id, rubro, is_default')
    .eq('id_company', companyId)
    .eq('is_draft', false)
    .order('is_default', { ascending: false })
    .order('created_at', { ascending: true });
  if (alListar) return null;
  const list = (brandsData as MarcaDeLaLista[] | null) ?? [];
  if (list.length === 0) return { id: null };

  const {
    data: { user },
    error: alAutenticar,
  } = await supabase.auth.getUser();
  if (alAutenticar || !user) return null;
  const { data: prof, error: alLeerPerfil } = await supabase
    .from('profiles')
    .select('current_brand_id')
    .eq('id', user.id)
    .maybeSingle();
  if (alLeerPerfil) return null;
  const elegida = elegirMarcaActiva(
    list,
    (prof as { current_brand_id?: string | null } | null)?.current_brand_id ?? null,
  );
  return elegida.id ? { id: elegida.id } : null;
}

/** Sólo el id de la marca activa — el contrato viejo, para los llamadores que no
 *  necesitan el rubro ni cuántas marcas hay. */
export async function resolveMarcaActivaId(
  companyId: string,
): Promise<string | null> {
  return (await resolveMarcaActiva(companyId)).id;
}
